import moment from "moment-timezone";
import { weekDayToNumber } from "../constants/weekDayToNumber";
import { parseScheduleSlot } from "./scheduleSlotUtils";

export function buildMakeSchedulePayload({
    studentId,
    selectedWeekdays,
    selectedTimes,
    startDate,
    weekDuration,
    timeZone,
}) {
    const startFrom = startDate.format("YYYY-MM-DD");

    const scheduleItems = Object.entries(selectedTimes)
        .filter(([weekday]) => selectedWeekdays.includes(weekday))
        .map(([key, value]) => {
            const { start: timeFrom, end: timeTo, quantity: parsedQuantity } = parseScheduleSlot(value);

            let adjustedTimeTo = timeTo;
            const [endHours, endMinutes] = timeTo.split(":").map(Number);
            if (endHours >= 24) {
                const normalizedHours = endHours - 24;
                adjustedTimeTo = `${normalizedHours.toString().padStart(2, "0")}:${endMinutes.toString().padStart(2, "0")}`;
            }

            let refDate = startDate.clone().isoWeekday(weekDayToNumber[key]);
            if (refDate.isBefore(startDate, "day")) {
                refDate.add(7, "days");
            }

            const startMomentUtc = moment.tz(
                `${refDate.format("YYYY-MM-DD")} ${timeFrom}:00`,
                "YYYY-MM-DD HH:mm:ss",
                timeZone,
            ).utc();
            const endMomentUtc = moment.tz(
                `${refDate.format("YYYY-MM-DD")} ${adjustedTimeTo}:00`,
                "YYYY-MM-DD HH:mm:ss",
                timeZone,
            ).utc();

            return {
                weekday: startMomentUtc.isoWeekday(),
                start_time: startMomentUtc.format("HH:mm:ss"),
                end_time: endMomentUtc.format("HH:mm:ss"),
                quantity: parsedQuantity,
            };
        });

    return {
        student_id: studentId,
        schedule_items: scheduleItems,
        note: null,
        start_from: startFrom,
        duration: weekDuration,
    };
}

const ISO_WEEKDAY_TO_SHORT = Object.fromEntries(
    Object.entries(weekDayToNumber).map(([short, iso]) => [iso, short]),
);

const WEEKDAY_NAME_TO_ISO = {
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
    sunday: 7,
};

function normalizeConflictWeekday(weekday) {
    if (typeof weekday === "number") {
        return weekday;
    }
    if (typeof weekday === "string") {
        const lowered = weekday.toLowerCase();
        if (WEEKDAY_NAME_TO_ISO[lowered]) {
            return WEEKDAY_NAME_TO_ISO[lowered];
        }
        const parsed = Number(weekday);
        return Number.isNaN(parsed) ? null : parsed;
    }
    return null;
}

export function groupScheduleConflictsByWeekday(conflicts, timeZone) {
    return conflicts.reduce((acc, conflict) => {
        const weekdayShort = ISO_WEEKDAY_TO_SHORT[normalizeConflictWeekday(conflict.weekday)];
        if (!weekdayShort) {
            return acc;
        }

        if (!acc[weekdayShort]) {
            acc[weekdayShort] = [];
        }

        acc[weekdayShort].push({
            ...conflict,
            formattedStart: moment.utc(conflict.start_time).tz(timeZone).format("DD.MM.YYYY HH:mm"),
        });
        return acc;
    }, {});
}

export function formatScheduleConflictMessage(conflictsForWeekday) {
    if (!conflictsForWeekday?.length) {
        return null;
    }

    const first = conflictsForWeekday[0];
    const restCount = conflictsForWeekday.length - 1;
    const suffix = restCount > 0 ? ` и ещё ${restCount} в периоде` : "";

    return `Слот занят: ${first.formattedStart} (${first.student_name})${suffix}`;
}
